import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Clock, Bike, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

const STATUS_CONFIG = {
    offered:  { label: 'Offered',  color: '#92400e', bg: '#fef3c7', icon: AlertCircle },
    accepted: { label: 'Accepted', color: '#065f46', bg: '#d1fae5', icon: CheckCircle2 },
    declined: { label: 'Declined', color: '#991b1b', bg: '#fee2e2', icon: XCircle },
};

const CASE_CONFIG = {
    'Case 1': { label: 'Case 1 · WithIn Rad',    color: '#4338ca', bg: '#eef2ff' },
    'Case 2': { label: 'Case 2 · Target Rad', color: '#0369a1', bg: '#e0f2fe' },
};

// ─── Single offer row ─────────────────────────────────────────────────────────
const OfferRow = ({ offer }) => {
    const status    = STATUS_CONFIG[offer.status] ?? STATUS_CONFIG.offered;
    const StatusIcon = status.icon;

    // available_case = what they qualify for NOW
    // offer_case     = what they were originally offered under
    const availCase    = offer.available_case ? (CASE_CONFIG[offer.available_case] ?? null) : null;
    const offeredCase  = CASE_CONFIG[offer.offer_case] ?? { label: offer.offer_case, color: '#64748b', bg: '#f1f5f9' };

    const isAvailable  = offer.is_available;
    const isOnline     = offer.isOnline;

    const rowBg        = isAvailable ? '#f0fdf4' : 'var(--color-background-primary)';
    const rowBorder    = isAvailable ? '0.5px solid #86efac' : '0.5px solid var(--color-border-tertiary)';

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            borderRadius: 10,
            background: rowBg,
            border: rowBorder,
        }}>
            {/* Avatar + online dot */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: '#000',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden',
                }}>
                    {offer.delivery_guy_photo
                        ? <img src={offer.delivery_guy_photo} alt={offer.delivery_guy_name}
                               style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <Bike size={15} color="#fff" />
                    }
                </div>
                {/* online/offline dot */}
                <div style={{
                    position: 'absolute', bottom: 1, right: 1,
                    width: 9, height: 9, borderRadius: '50%',
                    background: isOnline ? '#10b981' : '#94a3b8',
                    border: '2px solid var(--color-background-primary)',
                }} />
            </div>

            {/* Name + meta */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    fontWeight: 500, fontSize: 13,
                    color: 'var(--color-text-primary)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                    {offer.delivery_guy_name ?? '—'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                    {offer.delivery_guy_phone && (
                        <a href={`tel:${offer.delivery_guy_phone}`}
                           style={{ fontSize: 11, color: 'var(--color-text-secondary)', textDecoration: 'none' }}>
                            {offer.delivery_guy_phone}
                        </a>
                    )}
                    <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>
                        · {offer.active_orders ?? 0} active
                    </span>
                </div>
            </div>

            {/* Right column — two rows of pills */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>

                {/* Row 1: current availability */}
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {/* Which case they qualify for NOW (available_case) */}
                    {availCase ? (
                        <span style={{
                            fontSize: 10, fontWeight: 500,
                            padding: '2px 6px', borderRadius: 5,
                            background: availCase.bg, color: availCase.color,
                        }}>
                            {availCase.label}
                        </span>
                    ) : (
                        <span style={{
                            fontSize: 10, fontWeight: 500,
                            padding: '2px 6px', borderRadius: 5,
                            background: 'var(--color-background-secondary)',
                            color: 'var(--color-text-tertiary)',
                        }}>
                            no case
                        </span>
                    )}

                    {/* Available / unavailable badge */}
                    {isAvailable ? (
                        <span style={{
                            fontSize: 10, fontWeight: 500,
                            padding: '2px 6px', borderRadius: 5,
                            background: '#d1fae5', color: '#065f46',
                            border: '0.5px solid #86efac',
                        }}>
                            available
                        </span>
                    ) : (
                        <span style={{
                            fontSize: 10, fontWeight: 500,
                            padding: '2px 6px', borderRadius: 5,
                            background: '#fee2e2', color: '#991b1b',
                            border: '0.5px solid #fca5a5',
                        }}>
                            unavailable
                        </span>
                    )}
                </div>

                {/* Row 2: offer status + time */}
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    {/* Original offer case */}
                    <span style={{
                        fontSize: 10, fontWeight: 500,
                        padding: '2px 6px', borderRadius: 5,
                        background: offeredCase.bg, color: offeredCase.color,
                        opacity: 0.75,
                    }}>
                        {offeredCase.label}
                    </span>

                    {/* Status pill */}
                    <span style={{
                        fontSize: 10, fontWeight: 500,
                        padding: '2px 6px', borderRadius: 5,
                        background: status.bg, color: status.color,
                        display: 'flex', alignItems: 'center', gap: 3,
                    }}>
                        <StatusIcon size={9} />
                        {status.label}
                    </span>

                    {/* Time */}
                    <span style={{
                        fontSize: 10, color: 'var(--color-text-tertiary)',
                        display: 'flex', alignItems: 'center', gap: 2,
                    }}>
                        <Clock size={9} />
                        {offer.offered_at_exact ?? offer.offered_at}
                    </span>
                </div>
            </div>
        </div>
    );
};

// ─── Popup ────────────────────────────────────────────────────────────────────
const OrderOffersPopup = ({ offers = [], orderId, onClose }) => {
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [onClose]);

    const accepted      = offers.filter(o => o.status === 'accepted').length;
    const declined      = offers.filter(o => o.status === 'declined').length;
    const pending       = offers.filter(o => o.status === 'offered').length;
    const availableCount = offers.filter(o => o.is_available).length;

    const portal = typeof document !== 'undefined' ? document.body : null;
    if (!portal) return null;

    return ReactDOM.createPortal(
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(15,23,42,0.45)',
            backdropFilter: 'blur(3px)',
            animation: 'fadeIn 0.15s ease',
        }}>
            <div ref={ref} style={{
                background: '#fff',
                borderRadius: 16,
                width: '100%', maxWidth: 460, maxHeight: '80vh',
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
                overflow: 'hidden',
                border: '0.5px solid var(--color-border-tertiary)',
            }}>
                {/* Header */}
                <div style={{
                    padding: '14px 16px 12px',
                    borderBottom: '0.5px solid var(--color-border-tertiary)',
                    display: 'flex', alignItems: 'flex-start',
                    justifyContent: 'space-between', flexShrink: 0,
                }}>
                    <div>
                        <div style={{ fontWeight: 500, fontSize: 15, color: 'var(--color-text-primary)' }}>
                            Order offers
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                            #{String(orderId).slice(-6)}
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {accepted > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 6, background: '#d1fae5', color: '#065f46' }}>
                                {accepted} accepted
                            </span>
                        )}
                        {declined > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 6, background: '#fee2e2', color: '#991b1b' }}>
                                {declined} declined
                            </span>
                        )}
                        {pending > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 6, background: '#fef3c7', color: '#92400e' }}>
                                {pending} pending
                            </span>
                        )}
                        <span style={{
                            fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 6,
                            background: availableCount > 0 ? '#d1fae5' : '#fee2e2',
                            color:      availableCount > 0 ? '#065f46' : '#991b1b',
                        }}>
                            {availableCount} available
                        </span>
                        <button onClick={onClose} style={{
                            background: 'var(--color-background-secondary)',
                            border: '0.5px solid var(--color-border-tertiary)',
                            borderRadius: 8, padding: '5px 7px',
                            cursor: 'pointer', display: 'flex', alignItems: 'center',
                        }}>
                            <X size={13} color="var(--color-text-secondary)" />
                        </button>
                    </div>
                </div>

                {/* Legend */}
                <div style={{
                    padding: '8px 14px',
                    borderBottom: '0.5px solid var(--color-border-tertiary)',
                    display: 'flex', gap: 12, flexShrink: 0,
                    background: 'var(--color-background-secondary)',
                }}>
                    <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>
                        Top row = current availability · Bottom row = original offer
                    </span>
                </div>

                {/* Body */}
                <div style={{
                    overflowY: 'auto', flex: 1,
                    padding: '10px 12px',
                    display: 'flex', flexDirection: 'column', gap: 6,
                }}>
                    {offers.length === 0 ? (
                        <div style={{ textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 13, padding: '30px 0' }}>
                            No offers sent yet
                        </div>
                    ) : (
                        offers.map((offer, i) => (
                            <OfferRow key={`${offer.delivery_guy_id}-${i}`} offer={offer} />
                        ))
                    )}
                </div>
            </div>

            <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
        </div>,
        portal
    );
};

// ─── Badge ────────────────────────────────────────────────────────────────────
export const OffersBadge = ({ order }) => {
    const [open, setOpen] = useState(false);
    const count = order?.offers_count ?? 0;
    if (count === 0) return null;

    const hasAccepted    = order?.offers?.some(o => o.status === 'accepted');
    const availableCount = order?.offers?.filter(o => o.is_available).length ?? 0;

    const color = hasAccepted ? '#065f46' : '#92400e';
    const bg    = hasAccepted ? '#d1fae5' : '#fef3c7';

    return (
        <>
            <button
                onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                title="View offers"
                style={{
                    position: 'relative',
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 11, fontWeight: 500,
                    padding: '3px 8px', borderRadius: 20,
                    border: `0.5px solid ${color}60`,
                    background: bg, color,
                    cursor: 'pointer', marginTop: 4,
                    alignSelf: 'flex-start', width: 'fit-content',
                }}
            >
                <Bike size={11} />
                {count} offered

                {/* available drivers dot */}
                {availableCount > 0 && (
                    <span style={{
                        position: 'absolute', top: -5, right: -5,
                        minWidth: 14, height: 14, borderRadius: 99,
                        background: '#10b981', color: '#fff',
                        fontSize: 9, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '0 3px', border: '1.5px solid var(--color-background-primary)',
                        lineHeight: 1,
                    }}>
                        {availableCount}
                    </span>
                )}
            </button>

            {open && (
                <OrderOffersPopup
                    offers={order.offers ?? []}
                    orderId={order.unique_order_id}
                    onClose={() => setOpen(false)}
                />
            )}
        </>
    );
};

export default OrderOffersPopup;